import { TYPE_ORDER, typeLabel, typeColor, typeIndex } from '../constants/machineTypes';

// The floor's status vocabulary and grouping, kept OUT of the page component so that "there is
// exactly one status derivation" is a structural fact rather than a promise. Dashboard and Live Floor
// each used to derive status from a different payload, so the same machine could read ACTIVE on one
// page and HELD on the other. Anything that needs a machine's headline status imports `statusOf`.
//
// Pure functions, no React, no I/O — so they can be exercised directly against live API payloads.

/**
 * Headline status for one machine, from the engine overlay when present and the FOCAS-facing
 * `.state` only as a fallback.
 *
 * Precedence is deliberate: a tool change outranks the phase it interrupts (the machine is stopped
 * and the operator wants to know why), and a fault outranks everything.
 *
 * @param {object|null} live  LiveMachine entry from /floor/live, or null when no engine has it
 * @param {object|null} state `Machine.state`, the FOCAS-facing projection from /machines
 */
export function statusOf(live, state) {
  if (live?.toolChange) return { label: 'TOOL CHANGE', color: 'var(--cn-warning)' };
  const r = live?.running;
  if (r) {
    if (r.phase === 'faulted') return { label: 'FAULTED', color: 'var(--cn-danger)' };
    if (r.phase === 'starved') return { label: 'STARVED', color: 'var(--cn-warning)' };
    // SETUP must be checked BEFORE isActive: setup is an "active" phase (execution ACTIVE), so the
    // generic isActive→RUNNING check used to swallow it — a machine setting up read RUNNING with 0
    // parts / 0% cycle, which looks frozen. Surface SETUP distinctly instead.
    if (r.phase === 'setup') return { label: 'SETUP', color: 'var(--cn-gold)' };
    if (r.isActive) return { label: 'RUNNING', color: 'var(--cn-success)' };
    if (r.phase === 'complete') return { label: 'COMPLETE', color: 'var(--cn-muted)' };
    return { label: 'HELD', color: 'var(--cn-warning)' };
  }
  // No overlay: either no engine registered for this company, or the engine holds it idle.
  if (state?.faulted) return { label: 'FAULTED', color: 'var(--cn-danger)' };
  if (state?.alarm_code === 'UPSTREAM_CONSTRAINT') return { label: 'STARVED', color: 'var(--cn-warning)' };
  if (state?.execution === 'ACTIVE') return { label: 'RUNNING', color: 'var(--cn-success)' };
  return { label: 'IDLE', color: 'var(--cn-dimmed)' };
}

/**
 * Group joined rows by machine type in process order, unknown types trailing.
 *
 * ⚠️ The invariant is TOTALITY: every row in must appear in exactly one group out. The predecessor
 * mapped over a hardcoded type list instead, so a machine whose type wasn't on it landed in no group
 * and vanished — that is how SAND_CORE and SAND_MOLD went missing from the landing page while the
 * API was returning them the whole time. An unrecognised type sorts last; it is never filtered.
 */
export function groupByType(rows) {
  const buckets = new Map();
  for (const row of rows) {
    const t = String(row.type || '').toUpperCase();
    const key = TYPE_ORDER.includes(t) ? t : t || 'UNCLASSIFIED';
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(row);
  }
  return [...buckets.entries()]
    .map(([type, machines]) => ({
      type,
      label: typeLabel(type),
      color: typeColor(type),
      machines: [...machines].sort((a, b) => String(a.name).localeCompare(String(b.name))),
    }))
    .sort((a, b) => typeIndex(a.type) - typeIndex(b.type) || a.type.localeCompare(b.type));
}

/**
 * The live CNC alarm on a machine, or null.
 *
 * Deliberately NOT keyed on `faulted`: a WARNING-level alarm (2015 DOOR OPEN) leaves `faulted` false
 * and the machine cutting, and gating the UI on `faulted` is exactly what kept a live alarm off both
 * floor views — it was on the wire and visible in MachineMetrics while neither view showed it or
 * offered a way to clear it.
 *
 * `UPSTREAM_CONSTRAINT` is excluded because it is the engine's starve bookkeeping rather than a
 * control alarm: the STARVED badge already says it, and treating it as an alarm would put a red
 * banner on every machine waiting for its first transfer bin.
 *
 * Lives here, next to `statusOf`, for the same reason that does — one derivation, imported by every
 * surface that needs it, rather than one copy per page that can drift.
 */
export function alarmOf(live, state) {
  const r = live?.running;
  const code = r?.alarmCode ?? state?.alarm_code ?? null;
  if (!code || code === 'UPSTREAM_CONSTRAINT') return null;
  const faulted = !!(r?.faulted || state?.faulted);
  return {
    code: String(code),
    message: r?.alarmMessage ?? state?.alarm_message ?? null,
    level: r?.alarmLevel ?? state?.alarm_level ?? (faulted ? 'FAULT' : 'WARNING'),
    faulted,
  };
}
